/**
 * Utilitários de validação para o formulário de onboarding
 */

// CPF Validation
export function validateCPF(cpf: string): boolean {
  const cleaned = cpf.replace(/\D/g, '');

  if (cleaned.length !== 11) return false;

  // Check for known invalid patterns
  if (/^(\d)\1{10}$/.test(cleaned)) return false;

  // Validate first digit
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cleaned.charAt(i)) * (10 - i);
  }
  let remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cleaned.charAt(9))) return false;

  // Validate second digit
  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cleaned.charAt(i)) * (11 - i);
  }
  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cleaned.charAt(10))) return false;

  return true;
}

// CNPJ Validation
export function validateCNPJ(cnpj: string): boolean {
  const cleaned = cnpj.replace(/\D/g, '');

  if (cleaned.length !== 14) return false;

  // Check for known invalid patterns
  if (/^(\d)\1{13}$/.test(cleaned)) return false;

  // Validate first digit
  let size = cleaned.length - 2;
  let numbers = cleaned.substring(0, size);
  const digits = cleaned.substring(size);
  let sum = 0;
  let pos = size - 7;

  for (let i = size; i >= 1; i--) {
    sum += parseInt(numbers.charAt(size - i)) * pos--;
    if (pos < 2) pos = 9;
  }

  let result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (result !== parseInt(digits.charAt(0))) return false;

  // Validate second digit
  size = size + 1;
  numbers = cleaned.substring(0, size);
  sum = 0;
  pos = size - 7;

  for (let i = size; i >= 1; i--) {
    sum += parseInt(numbers.charAt(size - i)) * pos--;
    if (pos < 2) pos = 9;
  }

  result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (result !== parseInt(digits.charAt(1))) return false;

  return true;
}

// Email Validation
export function validateEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

// Phone Validation (Brazilian format)
export function validatePhone(phone: string): boolean {
  const cleaned = phone.replace(/\D/g, '');
  return cleaned.length === 10 || cleaned.length === 11;
}

// CEP Validation
export function validateCEP(cep: string): boolean {
  const cleaned = cep.replace(/\D/g, '');
  return cleaned.length === 8;
}

// CNAE Validation (basic format check)
export function validateCNAE(cnae: string): boolean {
  const cleaned = cnae.replace(/\D/g, '');
  return cleaned.length === 7;
}

// Date Validation
export function validateDate(dateStr: string): boolean {
  const date = new Date(dateStr);
  return !isNaN(date.getTime());
}

// Age Validation (must be 18+)
export function validateAge(birthDate: string, minAge: number = 18): boolean {
  const birth = new Date(birthDate);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }

  return age >= minAge;
}

// Percentage Validation (sum must equal 100)
export function validateSharePercentages(percentages: number[]): boolean {
  const sum = percentages.reduce((acc, curr) => acc + curr, 0);
  return Math.abs(sum - 100) < 0.01; // Allow for floating point errors
}

// Required Field Validation
export function validateRequired(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return true;
}

// Min Length Validation
export function validateMinLength(value: string, minLength: number): boolean {
  return value.trim().length >= minLength;
}

// Max Length Validation
export function validateMaxLength(value: string, maxLength: number): boolean {
  return value.trim().length <= maxLength;
}

// Positive Number Validation
export function validatePositiveNumber(value: number): boolean {
  return typeof value === 'number' && value > 0;
}

// Non-negative Number Validation
export function validateNonNegativeNumber(value: number): boolean {
  return typeof value === 'number' && value >= 0;
}

// File Size Validation (in bytes)
export function validateFileSize(fileSize: number, maxSizeBytes: number): boolean {
  return fileSize <= maxSizeBytes;
}

// File Type Validation
export function validateFileType(mimeType: string, allowedTypes: string[]): boolean {
  return allowedTypes.includes(mimeType);
}

// Allowed file types for the application
export const ALLOWED_FILE_TYPES = {
  pdf: 'application/pdf',
  jpeg: 'image/jpeg',
  png: 'image/png',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};

export const MAX_FILE_SIZES = {
  image: 5 * 1024 * 1024, // 5 MB
  document: 10 * 1024 * 1024, // 10 MB
};

// Validation Error Messages (Portuguese)
export const ERROR_MESSAGES = {
  required: 'Este campo é obrigatório',
  invalidCPF: 'CPF inválido',
  invalidCNPJ: 'CNPJ inválido',
  invalidEmail: 'E-mail inválido',
  invalidPhone: 'Telefone inválido',
  invalidCEP: 'CEP inválido',
  invalidCNAE: 'CNAE inválido',
  invalidDate: 'Data inválida',
  underAge: 'É necessário ter pelo menos 18 anos',
  invalidSharePercentage: 'A soma das participações deve ser 100%',
  minLength: (min: number) => `Mínimo de ${min} caracteres`,
  maxLength: (max: number) => `Máximo de ${max} caracteres`,
  invalidFileSize: (maxMB: number) => `Arquivo deve ter no máximo ${maxMB} MB`,
  invalidFileType: 'Tipo de arquivo não permitido',
  positiveNumber: 'O valor deve ser maior que zero',
  nonNegativeNumber: 'O valor não pode ser negativo',
};

// Composite Validator
export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

export function validate(
  value: unknown,
  rules: Array<{ validator: (value: unknown) => boolean; message: string }>
): ValidationResult {
  const errors: string[] = [];

  for (const rule of rules) {
    if (!rule.validator(value)) {
      errors.push(rule.message);
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}
